import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { useTimezone } from "hooks/useTimezone"

import { reportsService } from "services/reports/reports.service"
import { settingsService } from "services/settings/settings.service"
import { GgrSettings } from "services/settings/type"

import { GgrLimitScope } from "./ggr-history/types"
import { formatClipboardDate, formatUsd } from "./utils"

type GgrLimitSectionProps = {
  scope: GgrLimitScope
}

const scopeConfig = {
  CASINO: {
    label: "GGR",
    labelLower: "GGR",
    getSettings: () => settingsService.getGgr(),
    updateSettings: (p: GgrSettings) => settingsService.updateGgr(p),
    triggerSnapshot: () => reportsService.triggerGgrSnapshot()
  },
  SPORTSBOOK: {
    label: "Sportsbook GGR",
    labelLower: "sportsbook GGR",
    getSettings: () => settingsService.getSportsbookGgr(),
    updateSettings: (p: GgrSettings) => settingsService.updateSportsbookGgr(p),
    triggerSnapshot: () => reportsService.triggerSportsbookGgrSnapshot()
  }
}

export const GgrLimitSection: React.FC<GgrLimitSectionProps> = ({ scope }) => {
  const { formatDateTime } = useTimezone()
  const { t } = useTranslation("translation", { useSuspense: false })
  const { label, labelLower, getSettings, updateSettings, triggerSnapshot } = scopeConfig[scope]

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [snapshotRunning, setSnapshotRunning] = useState(false)

  const [enabled, setEnabled] = useState(false)
  const [limit, setLimit] = useState<number>(0) // cents

  const [snapshot, setSnapshot] = useState<GgrSettings["cache"] | null>(null)
  const [isLimitReached, setIsLimitReached] = useState(false)

  const [topUpInput, setTopUpInput] = useState<string>("")
  const [topUpAmount, setTopUpAmount] = useState<number>(0)

  const load = async () => {
    setLoading(true)
    try {
      const ggr = await getSettings()

      if (ggr) {
        setEnabled(ggr.enabled)
        setLimit(ggr.limit ?? 0)
        setSnapshot(ggr.cache ?? null)
        setIsLimitReached(!!ggr.isLimitReached)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const parseUsdInputToCents = (input: string): number => {
    const normalized = input.replace(/\s/g, "").replace(",", ".")
    const num = Number(normalized)
    if (Number.isNaN(num) || !Number.isFinite(num)) return 0
    return Math.round(num * 100)
  }

  const handleTopUpInputChange = (value: string) => {
    setTopUpInput(value)
    setTopUpAmount(parseUsdInputToCents(value))
  }

  const handlePresetTopUp = (multiplier: number) => {
    if (!enabled) return

    const baseCents = parseUsdInputToCents(topUpInput)
    if (!baseCents || baseCents <= 0) {
      toast.error("Enter an amount first (e.g. 1000), then choose a preset")
      return
    }

    const cents = Math.round(baseCents * multiplier)

    setTopUpAmount(cents)
    setTopUpInput(
      (cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    )
  }

  const handleCopySnapshot = async () => {
    if (!snapshot || !limit) {
      toast.error("Nothing to copy – snapshot or limit missing")
      return
    }

    const usedPercent = limit > 0 ? (snapshot.current / limit) * 100 : 0

    const text =
      `Current ${label} Snapshot:\n` +
      `Set Limit: ${formatUsd(limit)} USD\n` +
      `Used Limit: ${formatUsd(snapshot.current)} USD\n` +
      `Use Percent: ${usedPercent.toFixed(2)}%\n` +
      `Date: ${formatClipboardDate(snapshot.lastCheckedAt)}`

    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} snapshot copied to clipboard`)
    } catch (e) {
      console.error(e)
      toast.error("Failed to copy snapshot")
    }
  }

  const handleRunSnapshotNow = async () => {
    setSnapshotRunning(true)
    try {
      const result = await triggerSnapshot()
      if (result.ok) {
        toast.success(`${label} snapshot triggered`)
        await load()
        return
      }
      if (result.reason) toast.warning(`${label} snapshot skipped — ${result.reason}`)
    } finally {
      setSnapshotRunning(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)

    const finalLimit = enabled ? limit + topUpAmount : 0

    const success = await updateSettings({ enabled, limit: finalLimit })
    if (success) {
      toast.success(`${label} settings updated`)
      setLimit(finalLimit)
      setTopUpAmount(0)
      setTopUpInput("")
      await load()
    }

    setSaving(false)
  }

  const totalAfterTopUp = limit + (enabled ? topUpAmount : 0)
  const hasTopUp = topUpAmount > 0

  return (
    <section className="border border-[rgba(0,0,0,0.2)] dark:border-[rgba(255,255,255,0.1)] rounded-lg p-4 bg-white dark:bg-[#0C0C1A]">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-xl font-semibold">{label} Limit</h3>

        <button
          type="button"
          onClick={handleRunSnapshotNow}
          className="text-xs px-3 py-1 rounded border border-[rgba(169,78,206,0.8)] text-[rgba(169,78,206,1)] hover:bg-[rgba(169,78,206,0.08)] dark:text-white dark:border-[rgba(169,78,206,0.8)] disabled:opacity-60"
          disabled={loading || snapshotRunning}
          title={`Runs ${labelLower} snapshot immediately (admin)`}
        >
          {snapshotRunning ? t("ggr.runningSnapshot") : t("ggr.runSnapshot")}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading {labelLower} settings...</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <span className="text-base font-medium text-gray-800 dark:text-white">Enable {labelLower} limit for this tenant</span>
            <label className="relative inline-flex items-center w-11 h-6 cursor-pointer">
              <input type="checkbox" className="sr-only peer" checked={enabled} onChange={() => setEnabled((prev) => !prev)} />
              <div className="w-full h-full bg-[rgba(169,78,206,0.4)] peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-400 rounded-full peer peer-checked:bg-[rgba(169,78,206,1)] transition-colors duration-300 ease-in-out" />
              <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-[#9f90a7] rounded-full shadow-md transition-bg transform transition-transform duration-300 ease-in-out peer-checked:translate-x-5 peer-checked:bg-[#cccccc]" />
            </label>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-base font-medium text-gray-800 dark:text-white">Current {labelLower} limit</span>
            <span className="text-sm text-gray-700 dark:text-gray-300 font-semibold">{formatUsd(limit)} USD</span>
          </div>

          {snapshot && (
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 border rounded-md border-[rgba(0,0,0,0.1)] dark:border-[rgba(255,255,255,0.1)] p-3 flex flex-col gap-1">
              <div>
                Current month {labelLower} snapshot:&nbsp;
                <span className="font-semibold text-gray-900 dark:text-white">{formatUsd(snapshot.current)} USD</span>
              </div>
              <div>
                as of <span className="font-mono">{formatDateTime(snapshot.lastCheckedAt)}</span>
              </div>

              {isLimitReached && (
                <div className="mt-1 text-xs font-semibold text-red-500">{label} limit reached – new games are currently blocked.</div>
              )}

              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleCopySnapshot}
                  className="text-xs px-3 py-1 rounded border border-[rgba(169,78,206,0.8)] text-[rgba(169,78,206,1)] hover:bg-[rgba(169,78,206,0.08)] dark:text-white dark:border-[rgba(169,78,206,0.8)]"
                  disabled={!snapshot || !limit}
                >
                  {t("ggr.copySnapshot")}
                </button>
              </div>
            </div>
          )}

          <div className="mt-2 flex flex-col gap-3">
            <label className="text-base font-medium text-gray-800 dark:text-white">{t("ggr.addTopUp")}</label>

            <div className="flex flex-col gap-2">
              <input
                type="text"
                inputMode="decimal"
                className="input input-bordered w-full px-3 py-2 text-sm rounded-lg bg-white text-black border-black/10 placeholder:text-black/40 focus:outline-none focus:border-[rgba(169,78,206,0.8)] dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 dark:placeholder:text-white/40"
                placeholder="0.00 USD"
                value={topUpInput}
                onChange={(e) => handleTopUpInputChange(e.target.value)}
                disabled={!enabled}
              />

              <div className="flex flex-wrap gap-2 text-xs items-center">
                <span className="text-gray-500 dark:text-gray-400 mr-1">{t("ggr.quickOptions")}</span>

                <button
                  type="button"
                  className="px-2 py-1 rounded border border-black/10 bg-white text-[rgba(6,7,21,1)] hover:bg-black/[0.04] hover:border-[rgba(169,78,206,0.6)] disabled:opacity-50 disabled:hover:bg-white dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] dark:hover:border-[rgba(169,78,206,0.6)]"
                  onClick={() => handlePresetTopUp(10)}
                  disabled={!enabled}
                >
                  10% - 10x
                </button>

                <button
                  type="button"
                  className="px-2 py-1 rounded border border-black/10 bg-white text-[rgba(6,7,21,1)] hover:bg-black/[0.04] hover:border-[rgba(169,78,206,0.6)] disabled:opacity-50 disabled:hover:bg-white dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] dark:hover:border-[rgba(169,78,206,0.6)]"
                  onClick={() => handlePresetTopUp(7.5)}
                  disabled={!enabled}
                >
                  15% - 7.5x
                </button>

                <button
                  type="button"
                  className="px-2 py-1 rounded border border-black/10 bg-white text-[rgba(6,7,21,1)] hover:bg-black/[0.04] hover:border-[rgba(169,78,206,0.6)] disabled:opacity-50 disabled:hover:bg-white dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06] dark:hover:border-[rgba(169,78,206,0.6)]"
                  onClick={() => handlePresetTopUp(5)}
                  disabled={!enabled}
                >
                  20% - 5x
                </button>
              </div>
            </div>

            <div className="text-sm text-gray-500 dark:text-gray-400">
              <div>
                {t("ggr.amountToAdd")}&nbsp;
                <span className="font-semibold text-gray-900 dark:text-white">
                  {hasTopUp ? `${formatUsd(topUpAmount)} USD` : "0.00 USD"}
                </span>
              </div>
              <div>
                Total {labelLower} Limit after confirmation:&nbsp;
                <span className="font-semibold text-gray-900 dark:text-white">{formatUsd(totalAfterTopUp)} USD</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              className="btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : `Save ${label} Settings`}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
